import { Completion, CompletionContext } from "@codemirror/autocomplete"
import {
  DashboardNode,
  splitNodesByType,
} from "@features/builder/DashboardConfiguration/YamlEditor/jsonSchema"
import { AppNode } from "@features/builder/graph/types"

/*
 * Workarounds for `codemirror-json-schema` completion bugs for arrays nested
 * inside YAML objects. The dashboard schema already narrows `Nodes` by chart
 * `Type`, but the library does not reliably apply that narrowing in these
 * nested array contexts, so this file patches completion until the upstream
 * behavior is fixed.
 */

function getLineIndent(lineText: string) {
  return lineText.match(/^\s*/)?.[0].length ?? 0
}

function getParentKey(lineList: string[], indent: number): string | null {
  for (let index = lineList.length - 2; index >= 0; index -= 1) {
    const lineText = lineList[index] ?? ""
    const trimmed = lineText.trim()
    if (!trimmed || trimmed.startsWith("#")) {
      continue
    }

    const lineIndent = getLineIndent(lineText)
    if (lineIndent >= indent) {
      continue
    }

    if (trimmed.startsWith("-")) {
      continue
    }

    const keyMatch = trimmed.match(/^([^:#]+):/)
    if (keyMatch) {
      return keyMatch[1]?.trim() ?? null
    }
  }

  return null
}

function getChartType(lineList: string[], currentLineIndex: number): string | null {
  let chartItemIndex = -1

  for (let index = currentLineIndex; index >= 0; index -= 1) {
    const lineText = lineList[index] ?? ""
    const trimmed = lineText.trim()
    if (!trimmed || trimmed.startsWith("#")) {
      continue
    }

    if (!trimmed.startsWith("-")) {
      continue
    }

    const itemIndent = getLineIndent(lineText)
    const parentKey = getParentKey(lineList.slice(0, index + 1), itemIndent)
    if (parentKey === "Charts") {
      chartItemIndex = index
      break
    }
  }

  if (chartItemIndex === -1) {
    return null
  }

  const chartItemIndent = getLineIndent(lineList[chartItemIndex] ?? "")

  for (let index = chartItemIndex; index < lineList.length; index += 1) {
    const lineText = lineList[index] ?? ""
    const trimmed = lineText.trim()
    if (!trimmed || trimmed.startsWith("#")) {
      continue
    }

    const lineIndent = getLineIndent(lineText)
    if (index > chartItemIndex && lineIndent <= chartItemIndent) {
      break
    }

    const typeMatch = trimmed.match(/^(?:-\s*)?Type:\s*([^#\n]+?)\s*$/)
    if (typeMatch) {
      return typeMatch[1]?.trim() ?? null
    }
  }

  return null
}

function filterNodesByChartType(nodes: DashboardNode[], chartType: string | null) {
  const { defaultNodesNames, categoricalNodesNames, tableNodesNames } = splitNodesByType(nodes)

  let compatibleNodeNames = nodes.map((node) => node.label)
  if (chartType === "BarCategoricalChart") {
    compatibleNodeNames = categoricalNodesNames
  } else if (chartType === "Table") {
    compatibleNodeNames = tableNodesNames
  } else if (chartType) {
    compatibleNodeNames = defaultNodesNames
  }

  return compatibleNodeNames.filter(Boolean)
}

export function fixNodesCompletion(context: CompletionContext, appNodes: AppNode[]) {
  const nodes: DashboardNode[] = appNodes.flatMap((node) => {
    const label = node.data.label
    return label ? [{ label, type: node.type }] : []
  })
  // Custom completion for Nodes arrays
  const doc = context.state.doc
  const pos = context.pos
  const line = doc.lineAt(pos)
  const lineText = line.text
  const beforeCursor = lineText.slice(0, pos - line.from)
  const docText = doc.toString()

  if (!beforeCursor.match(/^\s*-\s*/)) {
    return null
  }

  // Just check: are we under "Nodes:" and properly indented?
  const textBeforeCursor = docText.slice(0, pos)
  const lines = docText.split("\n")
  const currentLineIndex = textBeforeCursor.split("\n").length - 1
  const currentLine = lines[currentLineIndex] ?? ""

  const currentIndent = getLineIndent(currentLine)

  const parentKey = getParentKey(lines.slice(0, currentLineIndex + 1), currentIndent)
  const isInNodes = parentKey?.toLowerCase() === "nodes"

  // If in Nodes array and typing array item
  if (isInNodes) {
    const wordMatch = beforeCursor.match(/^\s*-\s*(.*)$/)
    const currentWord = wordMatch ? wordMatch[1] ?? "" : ""
    const chartType = getChartType(lines, currentLineIndex)
    const compatibleNodeNames = filterNodesByChartType(nodes, chartType)

    const options: Completion[] = []

    for (const name of compatibleNodeNames) {
      if (!name.toLowerCase().includes(currentWord.toLowerCase())) {
        continue
      }

      options.push({
        label: name,
        type: "text",
        apply: currentWord ? name : ` ${name}`, // Add space if cursor right after -
      })
    }

    if (options.length > 0) {
      const from = pos - currentWord.length
      return { from, options }
    }
  }
  return null
}
